import { useState } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Product as D } from './productTokens';
import { MAX_UPLOAD_BYTES, prepareItemPhoto } from '../utils/prepareItemPhoto';

function PhotoUploader({ photos, setPhotos, minPhotos = 1, maxPhotos = 5, required = true, evidenceMode = false }) {
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || uploading) return;
    
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        // Evidence keeps the original file; only item photos are resized.
        if (evidenceMode && file.size > MAX_UPLOAD_BYTES) {
          throw new Error('Evidence photos must be 5 MB or smaller. Choose a smaller original photo.');
        }
        const uploadFile = evidenceMode ? file : await prepareItemPhoto(file);
        const formData = new FormData();
        formData.append('file', uploadFile);

        const endpoint = evidenceMode ? '/upload/dispute-evidence' : '/upload/photo';
        const response = await api.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const preview = URL.createObjectURL(uploadFile);
        return {
          filename: response.data.filename,
          preview
        };
      });

      const results = await Promise.allSettled(uploadPromises);
      const uploadedPhotos = results.filter(result => result.status === 'fulfilled').map(result => result.value);
      if (uploadedPhotos.length) {
        setPhotos([...photos, ...uploadedPhotos]);
        toast.success(`${uploadedPhotos.length} photo(s) uploaded`);
      }
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length) {
        const error = failures[0].reason;
        toast.error(error.response?.data?.detail || error.message || 'Failed to upload photos');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    const preview = photos[index]?.preview;
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {photos.map((photo, index) => (
          <div key={index} className="relative group">
            <img
              src={photo.preview}
              alt={`Upload ${index + 1}`}
              className="w-full h-32 object-cover rounded-lg"
              style={{ border: `2px solid ${D.border}` }}
            />
            <button
              type="button"
              onClick={() => removePhoto(index)}
              disabled={uploading}
              aria-label={`Remove photo ${index + 1}`}
              className="absolute top-2 right-2 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center"
              data-testid={`remove-photo-${index}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {photos.length < maxPhotos && (
          <label
            className="w-full h-32 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors"
            style={{ border: `2px dashed ${D.border}`, background: D.hover }}
          >
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="tt-radio-input"
              aria-label={evidenceMode ? 'Upload evidence photos' : 'Upload item photos'}
              disabled={uploading}
              data-testid="photo-input"
            />
            {uploading ? (
              <div className="text-center">
                <div className="w-8 h-8 rounded-full animate-spin mx-auto mb-2" style={{ border: `4px solid ${D.accent}`, borderTopColor: 'transparent' }}></div>
                <p className="text-sm" style={{ color: D.sub }}>Uploading...</p>
              </div>
            ) : (
              <div className="text-center">
                <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: D.dim }} />
                <p className="text-sm font-medium" style={{ color: D.text }}>Upload Photo</p>
                <p className="text-xs mt-1" style={{ color: D.sub }}>JPG, PNG, WebP</p>
              </div>
            )}
          </label>
        )}
      </div>

      <p className="text-xs" style={{ color: D.sub }}>
        {evidenceMode ? 'Original evidence photos: up to 5 MB each.' : 'Large photos are automatically resized. Up to 25 MB per photo.'}
      </p>

      <div className="flex items-center justify-between text-sm">
        <p style={{ color: D.sub }}>
          {photos.length} of {maxPhotos} photos uploaded
          {required && photos.length < minPhotos && (
            <span className="ml-2" style={{ color: D.error }}>(Minimum {minPhotos} required)</span>
          )}
        </p>
        {photos.length >= minPhotos && (
          <p className="flex items-center gap-1" style={{ color: D.success }}>
            <ImageIcon className="w-4 h-4" />
            Ready
          </p>
        )}
      </div>
    </div>
  );
}

export default PhotoUploader;
